import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api";

// UX-CP-01 §1 "every Control-Plane screen requires a kairoo_user + MFA; a gym Super Admin
// cannot access it" — mirrors AppDataContext's single-load-per-session pattern for the
// gym shell, but scoped to `/me` only (no branches/feature-entitlements — those are
// Data-Plane-only concepts, meaningless for a cross-tenant Kairoo session).
const KairooDataContext = createContext(null);

export function KairooDataProvider({ children }) {
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get("/me");
      if (data.user_type !== "kairoo") {
        // Defense in depth: a stale gym session cookie must never render the Control Plane.
        navigate("/app/dashboard");
        return;
      }
      setMe(data);
    } catch (e) {
      if (e.response?.status === 401) {
        navigate("/kairoo/login");
        return;
      }
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const logout = useCallback(async () => {
    await api.post("/auth/logout");
    navigate("/kairoo/login");
  }, [navigate]);

  return (
    <KairooDataContext.Provider value={{ me, loading, error, reload: load, logout }}>
      {children}
    </KairooDataContext.Provider>
  );
}

export function useKairooData() {
  return useContext(KairooDataContext);
}
